<!-- eslint-disable vue/no-v-html -->
<!-- it's okay to skip sanitizing because we don't modify received values, so there's no way for the user input to sneak in -->
<template>
  <div
    ref="root"
    class="help-center-prerendered__component"
    v-html="html"
  />
</template>

<script setup>
import { ref, onMounted, onUpdated } from 'vue'
import debounce from 'gleam-shared/helpers/debounce'
const RUNNING_ATTR = 'data-running-script'

defineProps({
  html: {
    type: String,
    required: true
  }
})

const root = ref(null)
const debouncedRunScripts = debounce(runScripts)

onMounted(debouncedRunScripts)
onUpdated(debouncedRunScripts)

function runScripts() {
  // scripts rendered by Vue won't run by themselves

  if (!root.value) {
    return
  }

  const sources = root.value.querySelectorAll('script')

  for (const source of sources) {
    if (source.hasAttribute(RUNNING_ATTR)) {
      continue
    }

    const script = document.createElement('script')
    script.setAttribute(RUNNING_ATTR, true)

    if (source.src) {
      script.src = source.src
    } else {
      script.textContent = source.textContent
    }

    source.replaceWith(script)
  }
}
</script>

<style lang="scss">
@import 'help-center/mixins';
@import 'help-center/prerendered';

@mixin add-media-modifier($modifier) {
  video.#{$modifier}, .#{$modifier} img {
    @content;
  }
}

.help-center-prerendered__component {
  @include help-center-font(14);
  color: var(--help-center-text);

  a:not(.styled-anchor) {
    @include help-center-highlight-link($offset: 2px, $userSelect: true);
    display: inline;
    align-items: center;
  }

  @include add-media-modifier(shadow-focused) {
    box-shadow: 0 0px 18px 0 #00000030;
  }

  @include add-media-modifier(shadow-diffused) {
    box-shadow: 0 0px 18px 0 #00000010;
  }

  @include add-media-modifier(halfway-sized) {
    max-width: 670px !important;
  }

  @include add-media-modifier(widget-sized) {
    max-width: 540px !important;
  }

  @include add-media-modifier(reel-sized) {
    max-width: 400px !important;
  }

  @include add-media-modifier(min-sized) {
    max-width: 300px !important;
  }

  @include add-media-modifier(full-sized) {
    max-width: initial !important;
  }

  .gcap, .gcap div {
    margin: 0;
  }

  picture {
    display: block;
  }

  img {
    width: 100%;
    height: auto;
    display: block;
  }

  > * {
    margin-top: 24px;
    margin-bottom: 24px;
  }

  > p {
    margin-top: 12px;
    margin-bottom: 12px;;
  }

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }

  > table {
    width: 100%;

    th {
      font-weight: var(--help-center-bold);
    }

    th, td {
      padding: 8px;
      vertical-align: top;
      text-align: left;
      border: 1px solid var(--help-center-border)
    }

    tr:nth-child(2n) {
      background: var(--help-center-card-bg);
    }
  }

  > hr {
    border: none;
    height: 1px;
    background-color: var(--help-center-border);
  }

  > pre code {
    display: block;
    border: 1px solid var(--help-center-border);
    padding: 20px;
    overflow-x: auto;
    background: var(--help-center-card-bg);
    font-family: Courier, monospace;
    border-radius: 6px;
  }

  > video {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 6px;
  }

  > picture, > figure {
    img {
      margin: 0 auto;
      border-radius: 6px;
    }
  }

  > figure {
    figcaption {
      color: var(--help-center-muted);
      margin: 12px 0 0;
      font-style: italic;
    }
  }

  > blockquote {
    border-left: 2px solid var(--help-center-highlight);
    padding-left: 12px;
  }

  > ul, ol {
    li {
      text-wrap: balance;
      margin-left: 2em;

      > * {
        margin-top: 12px;
        margin-bottom: 12px;
      }

      > *:first-child {
        margin-top: 0;
      }

      > *:last-child {
        margin-bottom: 0;
      }
    }
  }

  > center:has(.btn + .btn) {
    gap: 8px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }
}
</style>
